import {
  LitElement,
  css,
  html,
  classMap,
  styleMap,
  ref,
  createRef,
} from '@hh/global.js'

class HHDetailsModal extends LitElement {
  static properties = {
    breakpoint: {
      type: String,
    },
    label: {
      type: String,
    },
    selector: {
      type: String,
    },
    open: {
      type: Boolean,
      attribute: 'is-open',
      reflect: true,
    },
    forcedOpen: {
      attribute: 'open',
    },
    lockHeight: {
      type: Boolean,
      attribute: 'lock-height',
    },
    heightOffset: {
      type: Number,
      attribute: 'height-offset',
    },
    externalToggle: {
      type: String,
      attribute: 'external-toggle',
    },
    _height: {
      state: true,
    },
    lockScroll: {
      type: Boolean,
      attribute: 'lock-scroll',
    },
    grouped: {
      type: String
    }
  }

  $container = createRef()
  $details = createRef()

  constructor() {
    super()

    this.open = false
    this.lockHeight = this.getAttribute('lock-height') === 'true' ? true : false
    this.lockScroll = false
    this.heightOffset = 3
    this.breakpoint = '(min-width: 768px)'
    this.bodyListener = this.bodyListener.bind(this)
    this.grouped = false
    this.clickSearchInput = this.clickSearchInput.bind(this)
    this.doClickSearchInput = this.doClickSearchInput.bind(this)
    this.setSearchFocus = this.setSearchFocus.bind(this)
    this.resetModalFocus = this.resetModalFocus.bind(this)
    this.triggerElement = this.querySelector('[trigger]')
    this.id = Math.floor(Math.random() * 100000);
  }

  connectedCallback() {
    super.connectedCallback()

    if (this.isHeaderSearch) {
      this.querySelector('.header-search__input').addEventListener('click', this.doClickSearchInput)
      this.querySelector('.search-icon')?.addEventListener('click', this.setSearchFocus)
    }

    document.addEventListener('keydown', this.closeWithEscape.bind(this))
    document.addEventListener('modal-focus-reset', this.resetModalFocus)

    this.setExternalToggle()
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    if (this.isHeaderSearch) {
      this.querySelector('.header-search__input').removeEventListener('click', this.doClickSearchInput)
    }

    document.removeEventListener('keydown', this.closeWithEscape)
    document.removeEventListener('modal-focus-reset', this.resetModalFocus)
  }

  setExternalToggle() {
    this.externalToggleElement = document.querySelector(this.externalToggle)
    if (this.externalToggleElement) {
      this.externalToggleElement.addEventListener('click', this.toggle.bind(this))
    }
  }

  resetModalFocus(modal) {
    setTimeout(() => {
      document.querySelector(modal.detail.selector)?.focus()
    }, 0)
  }

  setSearchFocus() {
    setTimeout(() => {
      this.querySelector('.header-search__close')?.focus()
    }, 0)
  }

  get isHeaderSearch() {
    return this.classList.contains('header-search')
  }

  requestUpdate(name, value) {
    // Allows parents to close open modals
    if (name === 'forcedOpen') {
      this.toggle(false, !this.forcedOpen)
    }
    return super.requestUpdate(name, value)
  }

  interceptEvent(e) {
    e._intercepted = true
  }

  toggle(e = false, value = undefined) {
    if (
      e.type === 'keyup' &&
      (e.code.toUpperCase() !== 'ENTER' || e.code.toUpperCase() !== 'SPACE')
    ) {
      return true
    }

    e && e.preventDefault()

    this.open = typeof value === 'undefined' ? !this.open : value
    this[`${this.open ? 'add' : 'remove'}BodyListener`]()
    this.open && this.lockHeight && this.setHeight()
    this.open && this.broadcast()
    this.open && this.isHeaderSearch && this.clickSearchInput()

    this.dispatchEvent(
      new CustomEvent('modal-state', {
        bubbles: true,
        composed: true,
        detail: {
          value: this,
          state: this.open,
        },
      })
    )

    this.setAttribute('aria-expanded', this.open ? 'true' : 'false')

    this.updateGroup()
    this.triggerElement && this.triggerElement.setAttribute('aria-expanded', this.open)
  }

  updateGroup() {
    const groups = document.querySelectorAll(`hh-details-modal[grouped=${this.grouped}]`)
    if (groups && groups.length > 1) {
      groups.forEach(item => {
        if (item !== this) {
          item.open = false
        }
      })
    }

    if (this.externalToggleElement) {
      if (this.open) {
        this.externalToggleElement.setAttribute('is-open', '')
        this.externalToggleElement.classList.add('is-open')
      } else {
        this.externalToggleElement.classList.remove('is-open')
      }
    }
  }

  closeFromDetails(e) {
    const path = e.composedPath()

    if (
      path.some((element) => {
        return element.matches && element.matches('[data-close]')
      })
    ) {
      this.toggle()
    }
  }

  closeWithEscape(e) {
    if (e.code.toUpperCase() === 'ESCAPE' && this.open) {
      this.toggle()
    }


  }

  isMobile() {
    return !window.matchMedia(this.breakpoint).matches
  }

  addBodyListener() {
    if (this.lockScroll) {
      document.documentElement.classList.add('overflow-hidden');
    }
  }

  removeBodyListener() {
    if (this.lockScroll) {
      document.documentElement.classList.remove('overflow-hidden');
    }
  }

  bodyListener(e) {
    if (e._intercepted) return

    const swatchSlideOut = document.querySelector(
      'slide-panel[slideout-id="swatch-selector"][open="true"]'
    )

    if (this.open && this.classList.contains('header-search') && swatchSlideOut)
      return

    this.toggle()
  }

  setHeight() {
    const { top: parentTop = 0 } = this.parentNode.getBoundingClientRect() || {}
    const { top: detailsTop = 0 } =
      this.$details?.value.getBoundingClientRect() || {}

    this._height = this.isMobile()
      ? `calc(100vh - ${Math.floor(
        parentTop + detailsTop - this.heightOffset
      )}px)`
      : ''
  }

  broadcast() {
    this.dispatchEvent(
      new CustomEvent('modalOpened', {
        bubbles: true,
        composed: true,
        detail: {
          value: this,
        },
      })
    )
  }

  clickSearchInput(e) {
    if (this.querySelector('.header-search__input')?.value) {
      this.querySelector('.header-search__submit')?.removeAttribute('disabled')
    } else {
      this.querySelector('.header-search__submit')?.setAttribute('disabled', true)
    }
    setTimeout(() => {
      this.querySelector('.header-search__input')?.click()
    }, 150)
  }

  doClickSearchInput(e) {

  }

  render() {
    return html` <!-- @ts-ignore no-incompatible-type-binding -->
      <button
        aria-controls="MenuItem-${this.id}"
        aria-expanded="${this.open}"
        aria-haspopup="dialog"
        class="custom-details"
        aria-haspopup='dialog'
        ?open="${this.open}"
        @click="${this.interceptEvent}"
        ${ref(this.$container)}
      >
        <div
          id="MenuItem-${this.id}"
          class="custom-summary"
          @keyup="${this.toggle}"
          role="button"
          ${ref(this.$container)}
        >
          <slot
            @click="${this.toggle}"
            class="${classMap({ 'is-open': this.open })}"
            name="summary"
            tabindex="-1"
          ></slot>
        </div>
        <div
          class="custom-content"
          style="${styleMap({ height: this._height })}"
          ${ref(this.$details)}
        >
          <hh-focus-trap active="${this.open}">
            <slot
              name="details"
              @keyup="${this.closeWithEscape}"
              @click="${this.closeFromDetails}"
            ></slot>
          </hh-focus-trap>
        </div>
      </button>`;
  }

  static styles = css`
    .custom-summary:focus {
      outline: 0.2rem solid var(--hh-color-focus);
      outline-offset: var(--hh-focus-outline-offset);
    }

    .custom-summary {
      cursor: pointer;
      outline: none;
    }

    .custom-content {
      display: none;
    }

    .custom-details {
      background: var(--hh-background, transparent);
      padding: var(--hh-padding, 0);
      border: none;
    }

    .custom-details[open] .custom-content {
      display: block;
      position: var(--hh-position, absolute);
      top: var(--hh-top);
      left: 0;
      right: 0;
      bottom: var(--hh-bottom, 0);
      background: var(--hh-modal-background);
      height: var(--hh-modal-height);
      z-index: var(--hh-z-index-inner, 1);
      overflow: var(--hh-overflow, 'visible');
    }

    ::slotted([slot='details']) {
      width: 100%;
      display: flex;
    }

    :host {
      z-index: var(--hh-z-index, 100);
    }
  `
}

customElements.define('hh-details-modal', HHDetailsModal)
